import { ONBOARDING_GUIDE_STEPS } from '../../services/exports/Constants';

/**
 * The partner-onboarding wizard phases (FOO-6351 / PROD-483).
 *
 * Each phase maps to the server-persisted onboarding step(s) that back it
 * (plan §5). `completionStepKey` is the single step whose completion marks the
 * phase done — it is what the wizard reads on resume and what it marks after a
 * phase saves (save → markComplete → advance). `stepKeys` lists every step that
 * belongs to the phase for reference/mapping. The Online payment phase has no
 * onboarding step — its completion derives from payment-gateway verification
 * status (wired in C9), so it is flagged `derivesCompletionExternally`.
 *
 * Order note: Online payment sits last before Go Live. Round 1 put Domain there
 * on the grounds that DNS changes at a registrar are the step most likely to
 * stall; round 2 supersedes that (§7). Payment is the genuine last blocker — it
 * is what the partner has to connect, and it carries the go-live date message —
 * so it belongs closest to Go Live. The address only has to exist by then.
 *
 * This ordered list is the shared contract the per-phase children (C1–C10) plug
 * into: they render their body into the wizard shell and call
 * `wizard.savePhase(<key>)` when their save succeeds (no auto-advance).
 */
export const WizardPhaseKey = {
  Location: 'location',
  Identity: 'identity',
  Brand: 'brand',
  Operations: 'operations',
  Menu: 'menu',
  Domain: 'domain',
  GoLive: 'go_live',
  Payouts: 'payouts',
  ServiceFee: 'service_fee',
  BankAccount: 'bank_account',
};

export const WIZARD_PHASES = [
  {
    key: WizardPhaseKey.Location,
    completionStepKey: ONBOARDING_GUIDE_STEPS.Location,
    stepKeys: [ONBOARDING_GUIDE_STEPS.Location],
  },
  {
    key: WizardPhaseKey.Identity,
    completionStepKey: ONBOARDING_GUIDE_STEPS.Identity,
    stepKeys: [ONBOARDING_GUIDE_STEPS.Identity],
  },
  {
    key: WizardPhaseKey.Brand,
    completionStepKey: ONBOARDING_GUIDE_STEPS.LogoAndPrimaryColor,
    stepKeys: [ONBOARDING_GUIDE_STEPS.LogoAndPrimaryColor, ONBOARDING_GUIDE_STEPS.Gallery],
  },
  {
    key: WizardPhaseKey.Operations,
    completionStepKey: ONBOARDING_GUIDE_STEPS.OpenTimes,
    stepKeys: [
      ONBOARDING_GUIDE_STEPS.PickupTime,
      ONBOARDING_GUIDE_STEPS.DeliveryTime,
      ONBOARDING_GUIDE_STEPS.OpenTimes,
    ],
  },
  {
    key: WizardPhaseKey.Menu,
    completionStepKey: ONBOARDING_GUIDE_STEPS.ImportMenu,
    stepKeys: [ONBOARDING_GUIDE_STEPS.ImportMenu],
  },
  {
    key: WizardPhaseKey.Domain,
    completionStepKey: ONBOARDING_GUIDE_STEPS.CreateStorefront,
    stepKeys: [ONBOARDING_GUIDE_STEPS.CreateStorefront],
  },
  // No Business details phase any more (decision 2026-09-30): a cash-only restaurant
  // needs no legal or tax details to take orders. Go Live asks for the business name
  // only, and Online payment collects the rest — only for a hosted gateway, whose
  // hand-off is built from them.
  {
    key: WizardPhaseKey.Payouts,
    completionStepKey: null,
    stepKeys: [],
    derivesCompletionExternally: true,
  },
  {
    key: WizardPhaseKey.GoLive,
    completionStepKey: ONBOARDING_GUIDE_STEPS.EnableLiveOrders,
    stepKeys: [ONBOARDING_GUIDE_STEPS.EnableLiveOrders],
  },
  // The service-fee choice — after Go Live, a step of its own (it made the launch
  // screen too long). Only for a white-label partner's restaurant, whose fee is off
  // until enabled, and only for a partner viewer; the shell leaves it out otherwise.
  // Done once a decision exists, enabled or skipped.
  {
    key: WizardPhaseKey.ServiceFee,
    completionStepKey: null,
    stepKeys: [],
    derivesCompletionExternally: true,
  },
  // The payout bank account — after Go Live, because going live never waits for it:
  // a restaurant takes payments without one, and its money waits in the balance.
  // Only there for a restaurant we pay out (Stripe Connect or Adyen); the shell
  // leaves it out for the others. Its status from the provider is its completion.
  {
    key: WizardPhaseKey.BankAccount,
    completionStepKey: null,
    stepKeys: [],
    derivesCompletionExternally: true,
  },
];

export const TOTAL_PHASES = WIZARD_PHASES.length;

export const getPhase = (key) => WIZARD_PHASES.find((phase) => phase.key === key) ?? null;

export const getPhaseIndex = (key) => WIZARD_PHASES.findIndex((phase) => phase.key === key);

/**
 * The phases a PLG partner's restaurant owner can only read (ContactProviderGate):
 * Go Live always, and Online payment until the partner has linked a gateway —
 * after that the owner completes its hosted onboarding there. Nobody else has any.
 */
export function partnerLockedPhaseKeys(leavesDecisionsToPartner, gatewayLinked) {
  if (!leavesDecisionsToPartner) {
    return [];
  }

  return gatewayLinked ? [WizardPhaseKey.GoLive] : [WizardPhaseKey.Payouts, WizardPhaseKey.GoLive];
}
